import SimpleModal from "cpl-react-common/components/Modals/SimpleModal";
import "./CookiePreferencesModal.scss";
import { Field, Form, Formik } from "formik";
import { ClientPortalCookiePreferencesModel } from "services/api/cplauthapi/CPLAuthAPI";
import { useState } from "react";
import {
  analyticsCookieInformation,
  CookieInformation,
  essentialCookieInformation,
  functionalCookieInformation,
} from "./CookieInformation";
import useCookies from "hooks/useCookies";

type CookiePreferencesModalProps = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const CookiePreferencesModal = (props: CookiePreferencesModalProps) => {
  const [expandEssentialSection, setExpandEssentialSection] = useState(false);
  const [expandFunctionalSection, setExpandFunctionalSection] = useState(false);
  const [expandAnalyticsSection, setExpandAnalyticsSection] = useState(false);
  const { preferences } = useCookies();
  const { setPreferences } = useCookies();

  const onSubmit = (values: ClientPortalCookiePreferencesModel) => {
    setPreferences(values);
    props.setShowModal(false);
  };

  return (
    <SimpleModal
      modalTitle="Customise Cookie Preferences"
      showModal={props.showModal}
      dismissModalCallback={() => {
        props.setShowModal(false);
      }}
    >
      <p>
        We use some essential cookies to make this site work. We'd also like to set analytics cookies to understand how
        you use this site, and functional cookies to improve your user experience. Essential cookies are required for
        the site to run correctly but analytics and functional cookies will only be stored with your prior consent.
      </p>
      <hr />
      <Formik
        enableReinitialize={true}
        initialValues={
          preferences ?? new ClientPortalCookiePreferencesModel({ functionalEnabled: false, analyticsEnabled: false })
        }
        onSubmit={onSubmit}
      >
        {({ values }) => (
          <Form>
            <h4>Essential Cookies</h4>
            <p>
              These cookies are necessary for core functionality, such as security and network management. They always
              need to be active and do not store any personally identifiable data.
            </p>
            <div className="mb-2">
              <button
                type="button"
                className="cpl-btn"
                onClick={() => setExpandEssentialSection(!expandEssentialSection)}
                data-test-id="cookie-preferences-modal__essential-extra-information-button"
              >
                Extra Information
              </button>
              <ExpandableCookiesSection
                expanded={expandEssentialSection}
                cookieInformation={essentialCookieInformation}
              />
            </div>
            <div className="d-flex gap-2">
              <Field
                type="checkbox"
                disabled
                checked
                className="cookie-modal-checkbox"
                data-test-id="cookie-preferences-modal__essential-enabled-checkbox"
              ></Field>
              <label>Enabled</label>
            </div>
            <hr />
            <h4>Functional Cookies</h4>
            <p>
              These cookies are used to enhance user experience, such as enabling autosave on certain text inputs to
              prevent text being cleared even when leaving the page.
            </p>
            <div className="mb-2">
              <button
                type="button"
                className="cpl-btn"
                onClick={() => setExpandFunctionalSection(!expandFunctionalSection)}
                data-test-id="cookie-preferences-modal__functional-extra-information-button"
              >
                Extra Information
              </button>
              <ExpandableCookiesSection
                expanded={expandFunctionalSection}
                cookieInformation={functionalCookieInformation}
              />
            </div>
            <div className="d-flex gap-2">
              <Field
                type="checkbox"
                className="cookie-modal-checkbox"
                name="functionalEnabled"
                data-test-id="cookie-preferences-modal__functional-enabled-checkbox"
              ></Field>
              <label>Enabled</label>
            </div>
            <hr />
            <h4>Analytics Cookies</h4>
            <p>
              We use analytics to measure how you use our site to help us improve our user experience in the future.
              These cookies collect information about the pages you visit, how long you spend on each page, and what you
              click on.
            </p>
            <div className="mb-2">
              <button
                type="button"
                className="cpl-btn"
                onClick={() => setExpandAnalyticsSection(!expandAnalyticsSection)}
                data-test-id="cookie-preferences-modal__analytics-extra-information-button"
              >
                Extra Information
              </button>
              <ExpandableCookiesSection
                expanded={expandAnalyticsSection}
                cookieInformation={analyticsCookieInformation}
              />
            </div>
            <div className="d-flex gap-2">
              <Field
                type="checkbox"
                className="cookie-modal-checkbox"
                name="analyticsEnabled"
                data-test-id="cookie-preferences-modal__analytics-enabled-checkbox"
              ></Field>
              <label>Enabled</label>
            </div>
            <hr />
            <div className="d-flex justify-content-center gap-1 gap-md-3">
              <button
                className="cpl-btn cpl-btn-primary"
                data-test-id="cookie-preferences-modal__accept-all-button"
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit(new ClientPortalCookiePreferencesModel({ functionalEnabled: true, analyticsEnabled: true }));
                }}
              >
                Accept All
              </button>
              <button
                className="cpl-btn cpl-btn-primary"
                data-test-id="cookie-preferences-modal__save-preferences-button"
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit(
                    new ClientPortalCookiePreferencesModel({
                      functionalEnabled: values.functionalEnabled,
                      analyticsEnabled: values.analyticsEnabled,
                    })
                  );
                }}
              >
                Save Preferences
              </button>
              <button
                className="cpl-btn cpl-btn-primary"
                data-test-id="cookie-preferences-modal__reject-all-button"
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit(
                    new ClientPortalCookiePreferencesModel({ functionalEnabled: false, analyticsEnabled: false })
                  );
                }}
              >
                Reject All
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </SimpleModal>
  );
};

const ExpandableCookiesSection = ({
  expanded,
  cookieInformation,
}: {
  expanded: boolean;
  cookieInformation: CookieInformation[];
}) => {
  return (
    <div className={`cookie-section ${expanded ? "expanded" : "collapsed"}`}>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Purpose</th>
            <th>Expires</th>
          </tr>
        </thead>
        <tbody>
          {cookieInformation.map((x, index) => {
            return (
              <tr key={index}>
                <td>{x.name}</td>
                <td>{x.purpose}</td>
                <td>{x.expires}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CookiePreferencesModal;
